import { AlertTriangle, ArrowRight, CalendarClock, CheckCircle2, FileSignature, Hourglass, Percent, Search } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Kpi, Section } from '@/components/common/Blocks'
import { PageHeader } from '@/components/common/PageHeader'
import { Pager } from '@/components/common/Pager'
import { EmptyState } from '@/components/common/States'
import { usePagination } from '@/components/common/usePagination'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { fmtDate, fmtMoney } from '../shared/format'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useOffers, useOfferSummary } from './api'
import { QuoteTemplateButton } from './components/quotes/QuoteTemplateModal'
import { QUOTE_PERMS } from './quotes'
import { CustomerAvatar, OfferStatusBadge } from './components/CrmBits'
import type { Offer } from './types'
import { useCrm } from './ui'

const FILTERS = ['approval', 'open', 'expired', 'accepted', 'rejected', 'all'] as const
type Filter = (typeof FILTERS)[number]
const matches = (o: Offer, f: Filter) =>
  f === 'all' || (f === 'approval' ? o.flowStatus === 'manager_review' || o.flowStatus === 'owner_review' : f === 'open' ? o.status === 'draft' || o.status === 'sent' : f === 'expired' ? o.expired : o.status === f)

/** Tüm müşterilere verilen teklifler: bekleyenler, süresi dolanlar, sonuçlananlar. */
export function OffersPage() {
  const { t } = useTranslation()
  const { projects, customerById } = useCrm()
  const [projectId, setProjectId] = useState('all')
  const [filter, setFilter] = useState<Filter>('open')
  const [q, setQ] = useState('')
  const offers = useOffers()
  const { can } = useAuth()
  const summary = useOfferSummary({ projectId }).data

  const term = q.trim().toLocaleLowerCase('tr')
  const scoped = (offers.data ?? []).filter((o) => {
    const c = customerById.get(o.customerId)
    return (projectId === 'all' || c?.projectId === projectId) && (!term || `${c?.name ?? ''} ${o.number}`.toLocaleLowerCase('tr').includes(term))
  })
  const rows = scoped.filter((o) => matches(o, filter))

  const { pageRows, pager } = usePagination(rows, [projectId, filter, q])
  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.customers')}
        title={t('crm.offersPage.title')}
        description={t('crm.offersPage.subtitle')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {can(QUOTE_PERMS.template) ? <QuoteTemplateButton /> : null}
          <Select className="w-48" value={projectId} onChange={setProjectId} aria-label={t('crm.fields.project')} options={[{ value: 'all', label: t('crm.allProjects') }, ...projects.map((p) => ({ value: p.id, label: p.name }))]} />
          </div>
        }
      />
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <Kpi icon={Hourglass} tone="info" label={t('crm.offer.kpi.open')} value={summary ? fmtMoney(summary.openTotal) : '—'} footer={summary ? t('crm.offer.kpi.openCount', { count: summary.openCount }) : null} />
          <Kpi icon={AlertTriangle} tone="warning" label={t('crm.offersPage.expired')} value={summary?.expiredCount ?? '—'} footer={t('crm.offersPage.expiredHint')} />
          <Kpi icon={CheckCircle2} tone="success" label={t('crm.offer.kpi.accepted')} value={summary ? fmtMoney(summary.acceptedTotal) : '—'} />
          <Kpi icon={Percent} tone="primary" label={t('crm.offer.kpi.rate')} value={summary?.acceptanceRate == null ? '—' : `%${summary.acceptanceRate}`} footer={t('crm.offer.kpi.rateHint')} />
        </div>

        <Section
          title={t('crm.offersPage.listTitle')}
          icon={FileSignature}
          bodyClassName="p-0"
          actions={
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
              <div className="relative w-full sm:w-56">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                <Input className="h-8 pl-8 text-sm" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('crm.offersPage.search')} aria-label={t('crm.offersPage.search')} />
              </div>
              <div className="max-w-full overflow-x-auto scrollbar-none">
                <Tabs value={filter} onValueChange={(v) => setFilter(v as Filter)}>
                  <TabsList size="sm">
                    {FILTERS.map((f) => (
                      <TabsTrigger key={f} value={f}>
                        {t(`crm.offersPage.filter.${f}`)}
                        <span className="ml-1 tabular-nums text-muted-foreground">{scoped.filter((o) => matches(o, f)).length}</span>
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              </div>
            </div>
          }
        >
          {rows.length === 0 ? (
            <EmptyState icon={FileSignature} title={t('crm.offersPage.empty')} />
          ) : (
            <>
              <ul className="divide-y">
                {pageRows.map((o) => {
                  const c = customerById.get(o.customerId)
                  return (
                    <li key={o.id}>
                      <Link to={`/customers/${o.customerId}/offers`} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 transition-colors hover:bg-muted/40">
                        <span className="flex min-w-0 flex-1 basis-60 items-center gap-3">
                          <CustomerAvatar name={c?.name ?? '?'} className="size-9 text-xs" />
                          <span className="min-w-0">
                            <span className="block truncate font-medium">{c?.name ?? '—'}</span>
                            <span className="block truncate text-xs text-muted-foreground">
                              <span className="font-mono">{o.number}</span> · {fmtDate(o.date)} · {t('crm.offer.itemCount', { count: o.items.length })}
                            </span>
                          </span>
                        </span>
                        <span className={cn('inline-flex items-center gap-1 text-xs', o.expired ? 'font-medium text-destructive' : 'text-muted-foreground')}>
                          <CalendarClock className="size-3.5" aria-hidden />
                          {t('crm.offer.validShort', { date: fmtDate(o.validUntil) })}
                        </span>
                        <span className="ml-auto flex items-center gap-3">
                          <span className="font-semibold tabular-nums">{fmtMoney(o.total)}</span>
                          {o.expired ? <Badge tone="warning">{t('crm.offer.expired')}</Badge> : <OfferStatusBadge status={o.status} />}
                          <ArrowRight className="size-4 text-muted-foreground" aria-hidden />
                        </span>
                      </Link>
                    </li>
                  )
                })}
              </ul>
              <Pager {...pager} />
            </>
          )}
        </Section>
      </div>
    </>
  )
}
